import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ExternalLink, Shield, Info } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

export default function JurisdictionsPage() {
  const { t } = useI18n();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get("/state-sources")
      .then((r) => setSources(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const available = sources.filter((s) => s.available);
  const unavailable = sources.filter((s) => !s.available);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8" data-testid="jurisdictions-page">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-slate-900 text-white">
          <Shield size={18} />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{t("home.coverageTitle")}</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-3xl">{t("home.coverageBody")}</p>
        </div>
      </div>

      <div className="mt-6 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3">
        <Info size={16} className="text-blue-700 mt-0.5 shrink-0" />
        <p className="text-xs text-slate-700 leading-relaxed">
          Each link opens the <strong>official government inmate locator</strong> for that jurisdiction. If a state's online source isn't currently available, call the DOC directly.
        </p>
      </div>

      {loading && <p className="mt-6 text-sm text-slate-600">{t("common.loading")}</p>}

      {!loading && (
        <>
          <section className="mt-8">
            <h2 className="font-heading text-lg font-bold text-slate-900">
              Official locator available <span className="font-mono text-xs text-slate-500">({available.length})</span>
            </h2>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {available.map((s) => (
                <a
                  key={s.code}
                  href={s.locator_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`jurisdiction-${s.code}`}
                  className="group flex items-start justify-between rounded-md border border-slate-200 bg-white p-3 hover:border-slate-900 hover:shadow-sm transition"
                >
                  <div className="min-w-0 flex-1 pr-2">
                    <div className="font-medium text-slate-900 truncate">{s.state}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">
                      {t("home.officialAvailable")}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500 truncate">{s.notes || s.locator_url}</div>
                  </div>
                  <ExternalLink size={14} className="text-slate-400 group-hover:text-blue-600 shrink-0 mt-1" />
                </a>
              ))}
            </div>
          </section>

          {unavailable.length > 0 && (
            <section className="mt-10">
              <h2 className="font-heading text-lg font-bold text-slate-900">
                Online source not currently available <span className="font-mono text-xs text-slate-500">({unavailable.length})</span>
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                For these jurisdictions, contact the Department of Corrections directly to request inmate information.
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {unavailable.map((s) => (
                  <a
                    key={s.code}
                    href={s.locator_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`jurisdiction-${s.code}`}
                    className="group flex items-start justify-between rounded-md border border-slate-200 bg-slate-50 p-3 hover:border-slate-400 transition"
                  >
                    <div className="min-w-0 flex-1 pr-2">
                      <div className="font-medium text-slate-900 truncate">{s.state}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-amber-700">
                        {t("home.officialUnavailable")}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500 truncate">{s.notes || s.locator_url}</div>
                    </div>
                    <ExternalLink size={14} className="text-slate-400 group-hover:text-slate-700 shrink-0 mt-1" />
                  </a>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
